import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { AudioFile } from '@model/audioFile';
import { SearchApiService, SearchType } from '@service/api/search-api.service';
import { AutocompletionObject, AutocompletionType } from '@service/autocompletion.service';
import { LoggerService } from '@service/loggers/logger.service';
import { SearchData } from '@service/search.service';
import { SubscriptionsService } from '@service/subscriptions.service';
import { createClassObjectForAudioFile } from '@service/util/audioFileUtil';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SearchTextV5Service implements OnDestroy {

  private LOGGER_CLASSNAME = SearchTextV5Service.name;

  /**
   * Searching property
   */
  private get _searching(): boolean {
    return this._searchingSubject.value;
  }
  private set _searching(value: boolean) {
    if (this._searching !== value) {
        this._searchingSubject.next(value);
    }
  }
  get searching(): boolean {
    return this._searching;
  }
  private _searchingSubject = new BehaviorSubject<boolean>(false);
  public searching$ = this._searchingSubject.asObservable();


  /**
   * Search error
   */
  private get _searchError(): boolean {
    return this._searchErrorSubject.value;
  }
  private set _searchError(value: boolean) {
    if (this._searchError !== value) {
        this._searchErrorSubject.next(value);
    }
  }
  get searchError(): boolean {
    return this._searchError;
  }
  private _searchErrorSubject = new BehaviorSubject<boolean>(false);
  public searchError$ = this._searchErrorSubject.asObservable();


  /**
   * Search result
   */
  private get _searchResult(): AudioFile[] {
    return this._searchResultSubject.value;
  }
  private set _searchResult(value: AudioFile[]) {
    if (this._searchResult !== value) {
        this._searchResultSubject.next(value);
    }
  }
  get searchResult(): AudioFile[] {
    return this._searchResult;
  }
  private _searchResultSubject = new BehaviorSubject<AudioFile[]>(null);
  public searchResult$ = this._searchResultSubject.asObservable();

  /**
   * Last search input
   */

  private get _lastSearch(): SearchData {
    return this._lastSearchSubject.value;
  }
  private set _lastSearch(value: SearchData) {
    if (this._lastSearch !== value) {
        this._lastSearchSubject.next(value);
    }
  }
  get lastSearch(): SearchData {
    return this._lastSearch;
  }
  private _lastSearchSubject: BehaviorSubject<SearchData> = new BehaviorSubject<SearchData>(null);
  public lastSearch$: Observable<SearchData> = this._lastSearchSubject.asObservable();


  constructor(
    private loggerService: LoggerService,
    private searchApiService: SearchApiService,
    private subscriptionsService: SubscriptionsService
  ) {

  }

  private destroyed$ = new Subject<void>();
  ngOnDestroy(): void {
      this.searchStarts$.complete();

      this.destroyed$.next();
      this.destroyed$.complete();
  }

  public searchOnText(text: string) {
    this.clearLastSearch();

    const searchData = new SearchData();
    searchData.text = text;
    searchData.searchType = SearchType.SEARCH_BY_VALUE;


    const searchObservable: Observable<AudioFile[]> = this.searchApiService.searchOnText(text);

    this.handleSearchObservable(searchData, searchObservable);
    }

    public searchOnAutocompletion(autocompletionObject: AutocompletionObject) {
      this.clearLastSearch();

      const searchData = new SearchData();
      searchData.autocompletion = autocompletionObject;
      if (autocompletionObject.type === AutocompletionType.Song) {
        searchData.searchType = SearchType.AUTOCOMPLETION_SEARCH_ON_TITLE;
      } else {
        searchData.searchType = SearchType.AUTOCOMPLETION_SEARCH_ON_GROUP;
      }


      const searchObservable: Observable<AudioFile[]> = this.searchApiService.searchOnAutocompletion(autocompletionObject);

      this.handleSearchObservable(searchData, searchObservable);
      }

    public searchStarts$ = new Subject<SearchData>();
    private handleSearchObservable(searchData: SearchData, searchObservable: Observable<AudioFile[]>) {
      if (searchObservable) {

        this._lastSearch = searchData;

        this.searchStarts$.next(searchData);

        //set loading after emitting search starts -> finalize will be triggered and we need to keep the searching state for the latest request
        this._searching = true;

        searchObservable.pipe(
          finalize(() => {
            this.loggerService.debug(this.LOGGER_CLASSNAME, 'handleSearchObservable', 'finalize for ' + searchData.toString() + ' loading: ' + this.searching);
            if (!this.searching) {
              this.loggerService.error(this.LOGGER_CLASSNAME, 'handleSearchObservable', 'finalize for ' + searchData.toString()  + ' but we are not loading!');
            }
            this._searching = false;
          }),
          takeUntil(
            merge(
              this.destroyed$,
              this.searchStarts$
              )
          )
        ).subscribe(
            (data) => {
              this.loggerService.debug(this.LOGGER_CLASSNAME, 'handleSearchObservable', 'data received for ' + searchData.toString() + ': ' + data);

              const realObjects = data.map(res => createClassObjectForAudioFile(res));
              this._searchResult = realObjects;
            }
          ,
          (error : unknown) => {
            let errMsg = 'Error';
            if (error instanceof HttpErrorResponse){
              errMsg = (error.message) ? error.message :
              error.status ? `${error.status} - ${error.statusText}` : 'Server error';
            }
            this.loggerService.error(this.LOGGER_CLASSNAME, 'loadPlaylists error', errMsg);
            this._searchError = true;
          });
        }
    }


  public clearLastSearch() {
    this._lastSearch = null;
    this._searchResult = null;
    this._searching = false;
    this._searchError = null;
  }
}
